import AddIcon from '@atlaskit/icon/core/add';
import { colors } from '@atlaskit/theme';
import { token } from '@atlaskit/tokens';
import Tooltip from '@atlaskit/tooltip';
import { useAnalytics, getDatabaseContainerType } from '@databases/analytics';
import { Position } from '@databases/context-menu/type';
import { getFieldType } from '@databases/fields';
import { messages } from '@databases/intl/messages';
import { type HoverProps } from '@databases/react';
import { defaultStoreUtils } from '@databases/redux';
import { deselectAll } from '@databases/redux/cellSelections/actions';
import { addChangeToDraft } from '@databases/redux/changesets/actions';
import { selectDatabaseDraftByContentId } from '@databases/redux/changesets/selectors';
import { selectedBorder, TABLE_BORDER_RADIUS } from '@databases/styles';
import { DatabaseFieldType } from '@databases/types/database';
import { DatabaseChangeType } from '@databases/types/redux';
import { createField, getExistingFieldNames } from '@databases/utils/database/utils';
import { getUniqueFieldName } from '@databases/utils/field-name';
import React, { type FunctionComponent, useCallback } from 'react';
import { useIntl } from 'react-intl-next';
import styled, { css } from 'styled-components';

export const STYLED_DOT_RADIUS = 10;
export const StyledDot = styled.div<{ position: Position }>`
  opacity: 0;
  transition: opacity 0.2s;

  position: absolute;
  display: flex;
  justify-content: center;
  align-items: center;
  width: ${STYLED_DOT_RADIUS}px;
  height: ${STYLED_DOT_RADIUS}px;
  background-color: ${colors.N0};
  border-radius: ${token('radius.full', '50%')};
  color: transparent;

  &:hover {
    background-color: ${colors.B100};
    cursor: pointer;
    color: ${colors.N0};
    border: ${token('border.width.selected', '2px')} solid ${colors.N0};
    margin: ${token('space.negative.025', '-2px')};
  }

  &:not(:hover) {
    &:after {
      position: absolute;
      content: '';
      border: ${selectedBorder};
      border-radius: ${TABLE_BORDER_RADIUS};
      height: 0;
    }
  }

  && > * {
    height: 8px;
    width: 8px;

    svg {
      vertical-align: text-top;
      height: 100%;
      width: auto;
    }
  }
`;

// eslint-disable-next-line @atlaskit/design-system/ensure-design-token-usage
const InsertFieldDot = styled(StyledDot)`
  top: calc(50% - ${STYLED_DOT_RADIUS / 2}px);
  ${({ position }) =>
    position === Position.BEFORE
      ? // eslint-disable-next-line @atlaskit/design-system/ensure-design-token-usage
        css`
          left: -${STYLED_DOT_RADIUS / 2 + 1 / 2}px;
        `
      : // eslint-disable-next-line @atlaskit/design-system/ensure-design-token-usage
        css`
          right: -${STYLED_DOT_RADIUS / 2 + 1 / 2}px;
        `};
`;

export interface AddFieldDotProps {
  contentId: string;
  fieldId: string;
  canEditFields: boolean;
  position: Position;
  hoverProps?: HoverProps;
  currentViewId: string;
}

/**
 * Renders a dot that for adding a new field before or after the current field.
 */
const AddFieldDot: FunctionComponent<AddFieldDotProps> = ({
  contentId,
  fieldId,
  canEditFields,
  position,
  hoverProps,
  currentViewId,
}) => {
  const store = defaultStoreUtils.useStore();
  const dispatch = defaultStoreUtils.useDispatch();
  const client = useAnalytics();

  const { formatMessage } = useIntl();

  const handleInsertColumn = useCallback(
    async (e: React.MouseEvent) => {
      e.preventDefault();
      const databaseDraft = selectDatabaseDraftByContentId(store.getState(), {
        contentId,
        includeDeleted: true,
      });
      const newField = createField(DatabaseFieldType.TEXT);
      const fieldLabel = formatMessage(
        messages['field-settings.field-name-inline-edit-default-text'],
        {
          defaultFieldName: formatMessage(getFieldType(newField.type).label),
        }
      );
      const uniqueFieldName = getUniqueFieldName(
        fieldLabel,
        getExistingFieldNames(databaseDraft, newField)
      );

      const currentFieldIndex = databaseDraft.fields.indexOf(fieldId);
      const previousOrNextFieldIndex =
        position === Position.AFTER ? currentFieldIndex + 1 : currentFieldIndex;
      dispatch(
        addChangeToDraft(contentId, 'main', {
          type: DatabaseChangeType.ADD_FIELD,
          field: { ...newField, name: uniqueFieldName },
          index: previousOrNextFieldIndex,
          currentViewId: currentViewId,
        })
      );
      dispatch(deselectAll());
      await client.sendInsertColumnEvent({
        contentId: contentId,
        databaseContainer: getDatabaseContainerType(),
      });
    },
    [client, contentId, currentViewId, dispatch, fieldId, formatMessage, position, store]
  );

  if (!canEditFields) {
    return null;
  }

  return (
    <Tooltip
      content={() => formatMessage(messages['table-layout.insert-column-tooltip'])}
      position={position === Position.BEFORE ? 'left' : 'right'}
    >
      <InsertFieldDot
        onClick={handleInsertColumn}
        position={position}
        {...hoverProps}
        data-testid="insert-field-dot"
      >
        <AddIcon label="" LEGACY_size="small" />
      </InsertFieldDot>
    </Tooltip>
  );
};

export default AddFieldDot;
